import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Alert, CircularProgress, Chip, ButtonGroup, Paper, IconButton, useMediaQuery, useTheme, FormControlLabel, Switch } from '@mui/material';
import { Stage, Layer, Circle, Text, Group } from 'react-konva';
import { useAsientosRealtime } from "../../../hooks/useAsientosRealtime";
import { CloseOutlined, ShoppingCart, LocalOffer, ZoomIn, ZoomOut, RestartAlt } from '@mui/icons-material';
import { api } from '../../../services/api';
import { toast } from 'react-toastify';

const SelectUbicacionModal = ({
  open,
  onClose,
  tarifa,
  cantidadSeleccionada = 1,
  onConfirmar,
  seleccionActual,
}) => {
  const [layout, setLayout] = useState(null);
  const [cantidad, setCantidad] = useState(cantidadSeleccionada || 1);
  const [asientosSeleccionados, setAsientosSeleccionados] = useState([]);
  const [mesasSeleccionadas, setMesasSeleccionadas] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [isRealtimeEnabled, setIsRealtimeEnabled] = useState(true);

  const [escala, setEscala] = useState(1);
  const [posicion, setPosicion] = useState({ x: 0, y: 0 });

  const stageRef = useRef();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const totalEntradasAsignadas = Object.values(mesasSeleccionadas).reduce((sum, count) => sum + count, 0);

  const [mesaActiva, setMesaActiva] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const [showPopover, setShowPopover] = useState(false);

  const screenHeight = window.innerHeight;
  const isSmallScreen = screenHeight < 700;

  const CANVAS_WIDTH = isMobile ? Math.min(window.innerWidth - 40, 380) : 700;
  const CANVAS_HEIGHT = isMobile ?
    Math.min(window.innerHeight * 0.6, isSmallScreen ? 300 : 400) :
    450;


    /* =============== SOCKET ======= */
  const { isConnected, addUpdateCallback, removeUpdateCallback } = useAsientosRealtime(
    tarifa?.idevento,
    open && isRealtimeEnabled
  );

  // actualizacion de asientos en tiempo real
  const handleAsientoUpdate = useCallback((data) => {

    setLayout(prevLayout => {
      if (!prevLayout) return prevLayout;

      const newLayout = { ...prevLayout };

      // asientos indiv
      if (newLayout.asientos) {
        newLayout.asientos = newLayout.asientos.map(asiento =>
          asiento.id === data.asientoId
            ? { ...asiento, ocupado: data.ocupado, disponible: !data.ocupado }
            : asiento
        );
      }

      // asientos mesas
      if (newLayout.mesas) {
        newLayout.mesas = newLayout.mesas.map(mesa => ({
          ...mesa,
          asientos: mesa.asientos?.map(asiento =>
            asiento.id === data.asientoId
              ? { ...asiento, ocupado: data.ocupado, disponible: !data.ocupado }
              : asiento
          )
        }));
      }

      return newLayout;
    });

    // si el asiento recien ocupado esta seleccionado, deselecciono
    if (data.ocupado && asientosSeleccionados.includes(data.asientoId)) {
      setAsientosSeleccionados(prev => prev.filter(id => id !== data.asientoId));
      toast.warning('Un asiento que habías seleccionado acaba de ser ocupado', {
        position: "top-right",
        autoClose: 5000,
      });
    }
  }, [asientosSeleccionados]);

  // callback cada que se abre el modal
  useEffect(() => {
    if (open && isConnected) {
      addUpdateCallback(handleAsientoUpdate);
      return () => removeUpdateCallback(handleAsientoUpdate);
    }
  }, [open, isConnected, handleAsientoUpdate, addUpdateCallback, removeUpdateCallback]);

  const getNumeroAsiento = (asiento) => {
    let label = asiento.numero || asiento.asiento || asiento.id;
    if (typeof label === 'string') {
      const match = label.match(/(\d+)/);
      if (match) {
        label = match[1];
      }
    }
    return label;
  };

  //calcular cual es el centro del contenido para centrar el canvas
  const calcularBoundingBox = () => {
    if (!layout) return { minX: 0, minY: 0, maxX: 0, maxY: 0, centerX: 0, centerY: 0 };

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    let hasElements = false;

    // procesamos las mesas
    if (layout.mesas && layout.mesas.length > 0) {
      layout.mesas.forEach(mesa => {
        if (mesa.coordX !== undefined && mesa.coordY !== undefined) {
          hasElements = true;
          const mesaMinX = mesa.coordX;
          const mesaMaxX = mesa.coordX + (mesa.ancho || 50);
          const mesaMinY = mesa.coordY;
          const mesaMaxY = mesa.coordY + (mesa.alto || 50);

          minX = Math.min(minX, mesaMinX);
          maxX = Math.max(maxX, mesaMaxX);
          minY = Math.min(minY, mesaMinY);
          maxY = Math.max(maxY, mesaMaxY);
        }
      });
    }

    // procesamos los asientos
    if (layout.asientos && layout.asientos.length > 0) {
      layout.asientos.forEach(asiento => {
        if (asiento.coordX !== undefined && asiento.coordY !== undefined) {
          hasElements = true;
          const asientoSize = isMobile ? 30 : 40; // radio * 2
          const asientoMinX = asiento.coordX - asientoSize / 2;
          const asientoMaxX = asiento.coordX + asientoSize / 2;
          const asientoMinY = asiento.coordY - asientoSize / 2;
          const asientoMaxY = asiento.coordY + asientoSize / 2;

          minX = Math.min(minX, asientoMinX);
          maxX = Math.max(maxX, asientoMaxX);
          minY = Math.min(minY, asientoMinY);
          maxY = Math.max(maxY, asientoMaxY);
        }
      });
    }

    // si no hay items, usar lo de siempre
    if (!hasElements) {
      return {
        minX: 0,
        minY: 0,
        maxX: CANVAS_WIDTH,
        maxY: CANVAS_HEIGHT,
        centerX: CANVAS_WIDTH / 2,
        centerY: CANVAS_HEIGHT / 2
      };
    }

    const padding = 50;
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    return { minX, minY, maxX, maxY, centerX, centerY };
  };

  const centrarCanvas = () => {
    if (!layout) return;

    const boundingBox = calcularBoundingBox();
    const { minX, minY, maxX, maxY, centerX, centerY } = boundingBox;

    //calcular size del contenido
    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;

    // calcular escala para ver todo bien
    const scaleX = CANVAS_WIDTH / contentWidth;
    const scaleY = CANVAS_HEIGHT / contentHeight;
    const optimalScale = Math.min(scaleX, scaleY, 1.2);

    //calcular posicion para centrar todo
    const scaledCenterX = centerX * optimalScale;
    const scaledCenterY = centerY * optimalScale;

    const offsetX = (CANVAS_WIDTH / 2) - scaledCenterX;
    const offsetY = (CANVAS_HEIGHT / 2) - scaledCenterY;

    setEscala(optimalScale);
    setPosicion({ x: offsetX, y: offsetY });
  };

  useEffect(() => {
    if (open && tarifa) {
      cargarLayout();
      setCantidad(cantidadSeleccionada || 1);

      if (seleccionActual) {
        setAsientosSeleccionados(seleccionActual.asientos || []);

        if (seleccionActual.mesas && typeof seleccionActual.mesas === 'object') {
          setMesasSeleccionadas(seleccionActual.mesas);
        } else {
          setMesasSeleccionadas({});
        }
      } else {
        setAsientosSeleccionados([]);
        setMesasSeleccionadas({});
      }
    }
  }, [open, tarifa, seleccionActual, isMobile]);

  useEffect(() => {
    if (layout && open) {
      setTimeout(() => {
        centrarCanvas();
      }, 100);
    }
  }, [layout, open, CANVAS_WIDTH, CANVAS_HEIGHT]);

  useEffect(() => {
    if (open && cantidadSeleccionada && cantidadSeleccionada !== cantidad) {
      setCantidad(cantidadSeleccionada);
    }
  }, [cantidadSeleccionada, open]);

  // reset zoom
  useEffect(() => {
    if (!open) {
      setEscala(1);
      setPosicion({ x: 0, y: 0 });
    }
  }, [open]);

  const zoomIn = () => {
    const newScale = Math.min(escala * 1.2, 3);
    setEscala(newScale);
  };

  const zoomOut = () => {
    const newScale = Math.max(escala * 0.8, 0.3);
    setEscala(newScale);
  };

  const resetZoom = () => {
    if (layout) {
      centrarCanvas();
    } else {
      setEscala(1);
      setPosicion({ x: 0, y: 0 });
    }
  };

  const handleDragCanvas = (e) => {
    setPosicion({ x: e.target.x(), y: e.target.y() });
  };

  const incrementarCantidad = () => {
    const limite = tarifa?.restantes || 10;
    if (cantidad < limite) {
      const nuevaCantidad = cantidad + 1;
      setCantidad(nuevaCantidad);
    }
  };

  const decrementarCantidad = () => {
    if (cantidad > 1) {
      const nuevaCantidad = cantidad - 1;
      setCantidad(nuevaCantidad);

      if (layout?.tipo_seleccion === 'mesas_seleccion') {
        let entradasActuales = totalEntradasAsignadas;
        let nuevasSelecciones = { ...mesasSeleccionadas };

        const mesasConEntradas = Object.entries(nuevasSelecciones).sort(([, a], [, b]) => b - a);

        while (entradasActuales > nuevaCantidad && mesasConEntradas.length > 0) {
          for (let [mesaId, count] of mesasConEntradas) {
            if (entradasActuales <= nuevaCantidad) break;

            if (count > 1) {
              nuevasSelecciones[mesaId] = count - 1;
              entradasActuales--;
            } else {
              delete nuevasSelecciones[mesaId];
              entradasActuales--;
              const index = mesasConEntradas.findIndex(([id]) => id === mesaId);
              mesasConEntradas.splice(index, 1);
            }
          }
        }

        setMesasSeleccionadas(nuevasSelecciones);
      } else {
        if (asientosSeleccionados.length > nuevaCantidad) {
          const nuevosAsientos = asientosSeleccionados.slice(0, nuevaCantidad);
          setAsientosSeleccionados(nuevosAsientos);
        }
      }
    }
  };

  const cargarLayout = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.ubicacionesNew.getLayoutPorTarifa(tarifa.id);

      if (response.status === "success") {
        setLayout(response.data);
      } else {
        setError(response.message || "Error cargando mapa");
      }
    } catch (error) {
      console.error("Error cargando mapa:", error);
      setError("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  const handleAsientoClick = (asiento) => {
    if (!asiento.disponible) return;

    if (asientosSeleccionados.includes(asiento.id)) {
      setAsientosSeleccionados(prev => prev.filter(id => id !== asiento.id));
    } else {
      if (layout?.tipo_seleccion === 'mesas_seleccion') {
        if (asientosSeleccionados.length < cantidad) {
          setAsientosSeleccionados(prev => [...prev, asiento.id]);
        }
      } else {
        setAsientosSeleccionados(prev => [...prev, asiento.id]);
      }
    }
  };

  const handleMesaClick = (mesa, event) => {
    if (esTarifaMesaVIP(tarifa, layout)) {
      const totalMesasSeleccionadas = Object.keys(mesasSeleccionadas).length;
      if (totalMesasSeleccionadas >= 1 && !mesasSeleccionadas[mesa.id]) {
        return;
      }
    }

    if (layout?.tipo_seleccion === 'mesas_seleccion' && !mesa.esNumerada && mesa.mesaDisponible) {
      const stage = stageRef.current;
      const stageBox = stage.container().getBoundingClientRect();

      const mesaCentroX = (mesa.coordX + mesa.ancho / 2) * escala + posicion.x;
      const mesaCentroY = (mesa.coordY + mesa.alto / 2) * escala + posicion.y;

      const popoverX = stageBox.left + mesaCentroX;
      const popoverY = stageBox.top + mesaCentroY;

      setMesaActiva(mesa);
      setPopoverPosition({ x: popoverX, y: popoverY });
      setShowPopover(true);
    }
  };

  const renderAsientoMesa = (asiento, x, y) => {
    const isSelected = asientosSeleccionados.includes(asiento.id);
    const isDisponible = !asiento.ocupado && asiento.activo;

    let asientoLabel = asiento.asiento || `${asiento.id}`;

    const formatoLetraNumeros = asientoLabel.match(/^[A-Za-z]+(\d+)$/);
    if (formatoLetraNumeros) {
      asientoLabel = formatoLetraNumeros[1];
    }
    else {
      const match = asientoLabel.match(/-(\d+)$/);
      if (match) {
        asientoLabel = match[1];
      }
    }

    let fillColor = '#E8E8E8';
    let strokeColor = '#5C636A';

    if (isDisponible) {
      fillColor = isSelected ? '#ceceff' : '#c5eeb2';
      strokeColor = isSelected ? '#5656ff' : '#52c41a';
    }

    const radius = isMobile ? 12 : 15;

    return (
      <Group key={`asiento-mesa-${asiento.id}`}>
        <Circle
          x={x}
          y={y}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={1}
          onClick={() => isDisponible && handleAsientoClick({ ...asiento, disponible: isDisponible })}
          onTap={() => isDisponible && handleAsientoClick({ ...asiento, disponible: isDisponible })}
          style={{ cursor: isDisponible ? 'pointer' : 'not-allowed' }}
        />
        <Text
          x={x - (isMobile ? 6 : 7)}
          y={y - (isMobile ? 6 : 7)}
          width={isMobile ? 12 : 14}
          height={isMobile ? 12 : 14}
          align="center"
          verticalAlign="middle"
          text={getNumeroAsiento(asiento)}
          fontSize={isMobile ? 10 : 12}
          fill={
            asiento.ocupado ? "#5C636A" :
              isSelected ? "#5656FF" :
                asiento.disponible ? "#52c41a" : "white"
          }
          fontFamily="Bricolage Grotesque, sans serif"
          listening={false}
        />
        {isSelected && (
          <Circle
            x={x + (isMobile ? 6 : 8)}
            y={y - (isMobile ? 8 : 10)}
            radius={isMobile ? 3 : 4}
            fill="#5656ff"
            stroke="white"
            strokeWidth={1}
            listening={false}
          />
        )}
      </Group>
    );
  };

  const esTarifaMesaVIP = (tarifa, layout) => {
    return (
      layout?.tipo_seleccion === 'mesas_seleccion' &&
      (tarifa?.idubicacion === 530 || tarifa?.idubicacion === 538)
    );
  };

  const renderAsientoFila = (asiento, x, y) => {
    const isSelected = asientosSeleccionados.includes(asiento.id);
    const isDisponible = !asiento.ocupado && asiento.activo;

    let asientoLabel = getNumeroAsiento(asiento);

    const formatoLetraNumeros = asientoLabel.match(/^[A-Za-z]+(\d+)$/);
    if (formatoLetraNumeros) {
      asientoLabel = formatoLetraNumeros[1];
    }
    else {
      const match = asientoLabel.match(/-(\d+)$/);
      if (match) {
        asientoLabel = match[1];
      }
    }

    let fillColor = '#E8E8E8';
    let strokeColor = '#5C636A';

    if (isDisponible) {
      fillColor = isSelected ? '#ceceff' : '#c5eeb2';
      strokeColor = isSelected ? '#5656ff' : '#52c41a';
    }

    const radius = isMobile ? 15 : 20;

    return (
      <Group key={`asiento-fila-${asiento.id}`}>
        <Circle
          x={asiento.coordX}
          y={asiento.coordY}
          radius={radius}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={1}
          rotation={asiento.rotation || 0}
          onClick={() => isDisponible && handleAsientoClick({ ...asiento, disponible: isDisponible })}
          onTap={() => isDisponible && handleAsientoClick({ ...asiento, disponible: isDisponible })}
          style={{ cursor: isDisponible ? 'pointer' : 'not-allowed' }}
        />
        <Text
          x={asiento.coordX - (isMobile ? 12 : 16)}
          y={asiento.coordY - (isMobile ? 8 : 10)}
          width={isMobile ? 24 : 32}
          height={isMobile ? 16 : 20}
          align="center"
          verticalAlign="middle"
          text={getNumeroAsiento(asiento)}
          fontSize={isMobile ? 12 : 14}
          fill={
            asiento.ocupado ? "#5C636A" :
              isSelected ? "#5656FF" :
                asiento.disponible ? "#52c41a" : "white"
          }
          fontFamily="Bricolage Grotesque, sans serif"
          listening={false}
        />
        {isSelected && (
          <Circle
            x={asiento.coordX + (isMobile ? 10 : 12)}
            y={asiento.coordY - (isMobile ? 10 : 12)}
            radius={isMobile ? 3 : 4}
            fill="#5656ff"
            stroke="white"
            strokeWidth={1}
            rotation={asiento.rotation || 0}
            listening={false}
          />
        )}
      </Group>
    );
  };

  const renderMesa = (mesa) => {
    const entradasEnEstaMesa = mesasSeleccionadas[mesa.id] || 0;
    const tienentradas = entradasEnEstaMesa > 0;
    const centroX = mesa.coordX + mesa.ancho / 2;
    const centroY = mesa.coordY + mesa.alto / 2;

    const asientosVisibles = Array.isArray(mesa.asientos)
      ? mesa.asientos.filter(a => a.visible_cliente)
      : [];

    const esNumerada = asientosVisibles.length > 0;
    const asientosDisponiblesEnMesa = mesa.asientos_disponibles || 0;
    const mesaDisponible = asientosDisponiblesEnMesa > 0;

    const esMesaActiva = mesaActiva?.id === mesa.id;

    // si es seleccionada, un poquito mas grande
    const radioBase = Math.min(mesa.ancho, mesa.alto) / 2;
    const radioFinal = esMesaActiva ? radioBase * 1.15 : radioBase; // 15% mas grande

    const mesaShape = (
      <Circle
        x={centroX}
        y={centroY}
        radius={radioFinal}
        rotation={mesa.rotation || 0}
        fill={
          !mesaDisponible ? "#E8E8E8" :
            tienentradas ? "#ceceff" :
              "#c5eeb2"
        }
        stroke={
          !mesaDisponible ? "#5C636A" :
            tienentradas ? "#5656ff" :
              "#52c41a"
        }
        strokeWidth={esMesaActiva ? 3 : (tienentradas ? 2 : 1)}
        onClick={(e) => {
          if (!esNumerada && layout?.tipo_seleccion === 'mesas_seleccion' && mesaDisponible) {
            const konvaEvent = e.evt;
            handleMesaClick({ ...mesa, esNumerada, mesaDisponible }, konvaEvent);
          }
        }}
        onTap={(e) => {
          if (!esNumerada && layout?.tipo_seleccion === 'mesas_seleccion' && mesaDisponible) {
            const konvaEvent = e.evt;
            handleMesaClick({ ...mesa, esNumerada, mesaDisponible }, konvaEvent);
          }
        }}
        style={{
          cursor: (!esNumerada && layout?.tipo_seleccion === 'mesas_seleccion' && mesaDisponible) ? 'pointer' :
            !mesaDisponible ? 'not-allowed' : 'default'
        }}
      />
    );

    const mesasOrdenadas = layout?.mesas || [];
    const mesaIndex = mesasOrdenadas.findIndex(m => m.id === mesa.id) + 1;

    return (
      <Group key={`mesa-${mesa.id}`}>
        {mesaShape}

        <Text
          x={mesa.coordX + 5}
          y={mesa.coordY + (isMobile ? 25 : 30)}
          text={`Mesa ${mesaIndex.toString()}`}
          fontSize={isMobile ? 12 : 14}
          fill={
            !mesaDisponible ? "#5C636A" :
              tienentradas ? "#5656ff" :
                "#52c41a"
          }
          fontStyle="bold"
          fontFamily="Bricolage Grotesque, sans serif"
          listening={false}
        />

        <Text
          x={mesa.coordX + 5}
          y={mesa.coordY + (isMobile ? 38 : 45)}
          width={mesa.ancho - 15}
          wrap="word"
          text={
            !mesaDisponible ? "Ocupado" : 'Disponible'
          }
          fontSize={isMobile ? 10 : 12}
          fill={
            !mesaDisponible ? "#5C636A" :
              tienentradas ? "#5656ff" :
                "#52c41a"
          }
          fontFamily="Bricolage Grotesque, sans serif"
          listening={false}
        />

        {tienentradas && !esMesaActiva && (
          <>
            <Circle
              x={mesa.coordX + mesa.ancho - (isMobile ? 15 : 15)}
              y={mesa.coordY + (isMobile ? 12 : 10)}
              radius={isMobile ? 10 : 12}
              fill="#5656ff"
              stroke="#ffffff"
              strokeWidth={1}
              listening={false}
            />
            {<Text
              x={mesa.coordX + mesa.ancho - (isMobile ? 19 : 25)}
              y={mesa.coordY + (isMobile ? 8 : 7)}
              text={entradasEnEstaMesa.toString()}
              fontSize={isMobile ? 9 : 12}
              fill="white"
              fontStyle="bold"
              fontFamily="Bricolage Grotesque, sans serif"
              listening={false}
              width={isMobile ? 8 : 15}
              align="center"
            />}
          </>
        )}

        {layout?.tipo_seleccion === 'mesas_numeradas' &&
          esNumerada &&
          asientosVisibles.map((asiento, index) => {
            const radio = Math.min(mesa.ancho, mesa.alto) / 2 + (isMobile ? 20 : 25);
            const angulo = (2 * Math.PI * index) / asientosVisibles.length;
            const asientoX = centroX + radio * Math.cos(angulo);
            const asientoY = centroY + radio * Math.sin(angulo);

            const asientoConCoords = {
              ...asiento,
              coordX: asientoX,
              coordY: asientoY
            };

            return renderAsientoMesa(asientoConCoords, asientoX, asientoY);
          })}
      </Group>
    );
  };

  const renderAsientosIndividuales = () => {
    return layout?.asientos?.map(asiento => {
      if (asiento.fila && asiento.fila.trim() !== '') {
        return renderAsientoFila(asiento);
      } else {
        return renderAsientoFila(asiento);
      }
    });
  };

  const confirmarSeleccion = () => {
    if (layout?.tipo_seleccion === 'general') {
      onConfirmar({ cantidad, asientos: [], mesas: {} });
    } else if (layout?.tipo_seleccion === 'mesas_seleccion') {
      // tarifa vip, no enviamos asientos asi podemos usar el preimpreso false
      if (esTarifaMesaVIP(tarifa, layout) && !tarifa.preimpreso) {
        onConfirmar({
          cantidad: totalEntradasAsignadas,
          asientos: [],
          mesas: mesasSeleccionadas
        });
      } else {
        const asientosParaEnviar = convertirMesasAAsientos();
        onConfirmar({
          cantidad: totalEntradasAsignadas,
          asientos: asientosParaEnviar,
          mesas: mesasSeleccionadas
        });
      }
    } else if (layout?.tipo_seleccion === 'mesas_numeradas') {
      onConfirmar({ cantidad: asientosSeleccionados.length, asientos: asientosSeleccionados, mesas: {} });
    } else {
      onConfirmar({ cantidad: asientosSeleccionados.length, asientos: asientosSeleccionados, mesas: {} });
    }
    onClose();
  };

  const convertirMesasAAsientos = () => {
    const asientosParaEnviar = [];

    Object.entries(mesasSeleccionadas).forEach(([mesaId, cantidadEnMesa]) => {
      const mesa = layout.mesas.find(m => m.id == mesaId);
      if (mesa && mesa.asientos) {
        const asientosDisponibles = mesa.asientos
          .filter(a => !a.ocupado && a.activo)
          .slice(0, cantidadEnMesa)
          .map(a => a.id);

        asientosParaEnviar.push(...asientosDisponibles);
      }
    });

    return asientosParaEnviar;
  };

  const CircleIcon = ({ color }) => (
    <Box
      sx={{
        width: 12,
        height: 12,
        borderRadius: '50%',
        backgroundColor: color,
        border: '1px solid #999'
      }}
    />
  );

  const puedeConfirmar = () => {
    if (loading || error) return false;

    switch (layout?.tipo_seleccion) {
      case 'general':
        return true;
      case 'mesas_seleccion':
        return totalEntradasAsignadas > 0;
      case 'mesas_numeradas':
      default:
        return asientosSeleccionados.length > 0;
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    onClose();
  };

  if (!tarifa || !open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? '16px 16px 0 0' : '16px',
          boxShadow: '0 8px 32px rgba(44, 62, 80, 0.12)',
          bgcolor: '#ffff',
          height: isMobile ? '95vh' : '85vh',
          ...(isMobile && {
            position: 'fixed',
            bottom: 0,
            top: 'auto',
            left: 0,
            right: 0,
            transform: 'none',
            margin: 0,
            width: '100%'
          })
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: isMobile ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1,
        fontWeight: 700,
        fontSize: isMobile ? '20px' : '24px',
        color: '#0b0060',
        borderBottom: '1px solid #E4E4E7',
        mb: 2,
        padding: "8px 24px",
      }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="h6" sx={{
            fontWeight: 700,
            fontSize: isMobile ? '16px' : '22px',
            color: '#0b0060'
          }}>
            {tarifa.concepto}
          </Typography>
          {isMobile && (
            <Box sx={{
              left: 0,
              right: 0,
              display: 'flex',
              gap: 1,
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <Chip
                label="Disponible"
                size="small"
                sx={{
                  bgcolor: '#c5eeb2',
                  color: '#52c41a',
                  fontWeight: 500,
                  fontSize: '11px'
                }}
              />
              <Chip
                label="Seleccionado"
                size="small"
                sx={{
                  bgcolor: '#ceceff',
                  color: '#5656ff',
                  fontWeight: 500,
                  fontSize: '11px'
                }}
              />
              <Chip
                label="Ocupado"
                size="small"
                sx={{
                  bgcolor: '#E8E8E8',
                  color: '#5C636A',
                  fontWeight: 500,
                  fontSize: '11px'
                }}
              />
            </Box>
          )}
        </Box>
        <Button
          onClick={onClose}
          size="small"
          sx={{ minWidth: 'auto', p: 1 }}
        >
          <CloseOutlined />
        </Button>
      </DialogTitle>

      <DialogContent sx={{
        pt: isMobile ? 1 : 3,
        position: 'relative',
        overflow: 'hidden',
        px: isMobile ? 1 : 3,
        height: isMobile ?
          (isSmallScreen ? 'calc(95vh - 140px)' : 'calc(95vh - 120px)') :
          'calc(85vh - 120px)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={40} />
            <Typography sx={{ ml: 2 }}>Cargando mapa...</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box sx={{
            display: 'flex',
            gap: isMobile ? 0 : 4,
            alignItems: 'flex-start',
            minHeight: 0,
            height: "100%",
            flexDirection: isMobile ? 'column' : 'row',
            overflow: 'hidden'
          }}>
            {/* Panel izquierdo - desktop */}
            {!isMobile && (
              <Box sx={{
                width: 320,
                bgcolor: '#F9FAFB',
                borderRadius: '10px',
                p: 3,
                boxShadow: '0 1px 3px rgba(44,62,80,0.07)',
                border: '1px solid #e5e7eb',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                height: "100%",
                flexShrink: 0,
                overflowY: 'auto',
                overflowX: 'hidden'
              }}>

                {/* Leyenda Desktop */}
                <Box sx={{ display: 'flex', flexDirection: "column", gap: 2, mb: 2 }}>
                  <Typography sx={{
                    fontWeight: 700,
                    fontSize: '16px',
                    color: '#5656ff',
                  }}>Leyenda</Typography>
                  <Chip
                    icon={<CircleIcon color="#52C41A" />}
                    label="Disponible"
                    size="small"
                    sx={{ bgcolor: "#C5EBB2", color: "#52C41A", fontWeight: 600 }}
                  />
                  <Chip
                    icon={<CircleIcon color="#5656ff" />}
                    label="Seleccionado"
                    size="small"
                    sx={{ bgcolor: "#ceceff", color: "#5656ff", fontWeight: 600 }}
                  />
                  <Chip
                    icon={<CircleIcon color="#5C636A" />}
                    label="Ocupado"
                    size="small"
                    sx={{ bgcolor: "#E8E8E8", color: "#5C636A", fontWeight: 600 }}
                  />
                </Box>

                <Box sx={{
                  display: 'flex',
                  gap: 0.5,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  bgcolor: 'white',
                  borderRadius: '8px',
                  p: 1,
                  border: '1px solid #e5e7eb'
                }}>
                  <IconButton size="small" onClick={zoomOut} sx={{ width: 32, height: 32 }}>
                    <ZoomOut fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" sx={{ mx: 1, minWidth: '60px', textAlign: 'center', fontSize: '12px' }}>
                    {Math.round(escala * 100)}%
                  </Typography>
                  <IconButton size="small" onClick={zoomIn} sx={{ width: 32, height: 32 }}>
                    <ZoomIn fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={resetZoom} sx={{ ml: 0.5, width: 32, height: 32 }}>
                    <RestartAlt fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}

            {/* Canvas */}
            <Box sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              width: '100%',
              maxWidth: isMobile ? '100%' : '700px',
              overflow: 'hidden',
              mx: 'auto'
            }}>
              {isMobile && (
                <Box sx={{
                  position: 'absolute',
                  top: -50,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 2
                }}>
                  <ButtonGroup variant="outlined" size="small">
                    <Button
                      onClick={decrementarCantidad}
                      disabled={cantidad <= 1}
                      sx={{ color: "#0b0060", borderColor: "#0b0060", fontSize: '12px' }}
                    >
                      -
                    </Button>
                    <Button
                      disabled
                      sx={{
                        color: "#0b0060",
                        borderColor: "#0b0060",
                        '&.Mui-disabled': { color: "#0b0060" },
                        fontSize: '12px'
                      }}
                    >
                      {cantidad}
                    </Button>
                    <Button
                      onClick={incrementarCantidad}
                      sx={{ color: "#0b0060", borderColor: "#0b0060", fontSize: '12px' }}
                    >
                      +
                    </Button>
                  </ButtonGroup>

                  <Typography variant="caption" sx={{ color: "#5656ff", fontWeight: 600 }}>
                    {layout?.tipo_seleccion === 'mesas_seleccion'
                      ? `${totalEntradasAsignadas}`
                      : `${asientosSeleccionados.length}/${cantidad}`}
                  </Typography>
                </Box>
              )}

              <Paper elevation={0} sx={{
                bgcolor: '#f9fafb',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                width: '100%',
                maxWidth: CANVAS_WIDTH,
                height: CANVAS_HEIGHT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
                position: 'relative',
                overflow: 'hidden'
              }}
                data-canvas-container="true">
                <Stage
                  width={CANVAS_WIDTH}
                  height={CANVAS_HEIGHT}
                  ref={stageRef}
                  scaleX={escala}
                  scaleY={escala}
                  x={posicion.x}
                  y={posicion.y}
                  draggable
                  onDragEnd={handleDragCanvas}
                  style={{
                    background: 'transparent',
                    display: 'block',
                    cursor: escala > 1 ? 'grab' : 'default',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    touchAction: 'none'
                  }}
                  onMouseDown={(e) => {
                    if (escala > 1) {
                      e.target.getStage().container().style.cursor = 'grabbing';
                    }
                  }}
                  onMouseUp={(e) => {
                    if (escala > 1) e.target.getStage().container().style.cursor = 'grab';
                  }}
                >
                  <Layer>
                    {layout?.mesas?.map(mesa => renderMesa(mesa))}
                    {renderAsientosIndividuales()}
                  </Layer>
                </Stage>

                {showPopover && mesaActiva && (
                  <>
                    <Box
                      sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1400,
                        backgroundColor: 'transparent'
                      }}
                      onClick={() => {
                        setShowPopover(false);
                        setMesaActiva(null);
                      }}
                    />

                    {/* Popover */}
                    <Paper
                      elevation={8}
                      sx={{
                        position: 'fixed',
                        left: Math.min(popoverPosition.x - 80, window.innerWidth - 180),
                        top: Math.max(popoverPosition.y - 120, 20),
                        zIndex: 1500,
                        bgcolor: 'white',
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb',
                        minWidth: 160,
                        p: 2,
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                      }}
                    >
                      {/* Header del popover */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{
                          fontWeight: 600,
                          color: '#0b0060',
                          fontSize: '14px'
                        }}>
                          Mesa {layout?.mesas?.findIndex(m => m.id === mesaActiva.id) + 1}
                        </Typography>
                        <Typography variant="caption" sx={{
                          color: '#16a34a',
                          fontSize: '10px',
                          display: 'block',
                          fontWeight: 500
                        }}>
                          Valor unitario: ${tarifa.precio}
                        </Typography>
                      </Box>

                      {/* Controles de cantidad */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: '#f8f9fa',
                        borderRadius: '8px',
                        p: 1
                      }}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const entradasActuales = mesasSeleccionadas[mesaActiva.id] || 0;
                            if (esTarifaMesaVIP(tarifa, layout)) {
                              if (entradasActuales === 1) {
                                const nuevaSeleccion = { ...mesasSeleccionadas };
                                delete nuevaSeleccion[mesaActiva.id];
                                setMesasSeleccionadas(nuevaSeleccion);
                              }
                            } else {
                              if (entradasActuales === 1) {
                                const nuevaSeleccion = { ...mesasSeleccionadas };
                                delete nuevaSeleccion[mesaActiva.id];
                                setMesasSeleccionadas(nuevaSeleccion);
                              } else if (entradasActuales > 1) {
                                setMesasSeleccionadas(prev => ({
                                  ...prev,
                                  [mesaActiva.id]: prev[mesaActiva.id] - 1
                                }));
                              }
                            }
                          }}
                          disabled={!mesasSeleccionadas[mesaActiva.id]}
                          sx={{
                            bgcolor: 'white',
                            border: '1px solid #e5e7eb',
                            width: 32,
                            height: 32,
                            '&:hover': { bgcolor: '#f3f4f6' }
                          }}
                        >
                          <Typography sx={{ fontWeight: 600, color: '#ef4444' }}>−</Typography>
                        </IconButton>

                        <Typography variant="h6" sx={{
                          fontWeight: 600,
                          color: '#0b0060',
                          minWidth: 24,
                          textAlign: 'center'
                        }}>
                          {mesasSeleccionadas[mesaActiva.id] || 0}
                        </Typography>

                        <IconButton
                          size="small"
                          onClick={() => {
                            const entradasActuales = mesasSeleccionadas[mesaActiva.id] || 0;
                            if (esTarifaMesaVIP(tarifa, layout)) {
                              if (entradasActuales < 1) {
                                setMesasSeleccionadas(prev => ({
                                  ...prev,
                                  [mesaActiva.id]: 1
                                }));
                              }
                            } else {
                              const asientosDisponibles = mesaActiva.asientos_disponibles || 0;
                              if (entradasActuales < asientosDisponibles) {
                                setMesasSeleccionadas(prev => ({
                                  ...prev,
                                  [mesaActiva.id]: (prev[mesaActiva.id] || 0) + 1
                                }));
                              }
                            }
                          }}
                          disabled={
                            esTarifaMesaVIP(tarifa, layout)
                              ? (mesasSeleccionadas[mesaActiva.id] || 0) >= 1
                              : (mesasSeleccionadas[mesaActiva.id] || 0) >= (mesaActiva.asientos_disponibles || 0)
                          }
                          sx={{
                            bgcolor: 'white',
                            border: '1px solid #e5e7eb',
                            width: 32,
                            height: 32,
                            '&:hover': { bgcolor: '#f3f4f6' }
                          }}
                        >
                          <Typography sx={{ fontWeight: 600, color: '#10b981' }}>+</Typography>
                        </IconButton>
                      </Box>
                    </Paper>
                  </>
                )}

                {isMobile && (
                  <Box sx={{
                    position: 'absolute',
                    top: isSmallScreen ? 10 : 15,
                    right: isSmallScreen ? 10 : 15,
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 0.5,
                    bgcolor: 'rgba(255,255,255,0.95)',
                    borderRadius: '8px',
                    p: 0.5,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    border: '1px solid rgba(0,0,0,0.1)',
                    pointerEvents: 'auto',
                    zIndex: 10,
                    height: 32,
                    alignItems: 'center'
                  }}>
                    <IconButton
                      size="small"
                      onClick={zoomOut}
                      sx={{
                        bgcolor: 'white',
                        boxShadow: 1,
                        width: 26,
                        height: 26,
                        minWidth: 26,
                        '&:hover': { bgcolor: '#f5f5f5' },
                        p: 0
                      }}
                    >
                      <ZoomOut fontSize="small" />
                    </IconButton>

                    <Typography variant="caption" sx={{
                      px: 1,
                      fontSize: '9px',
                      lineHeight: 1,
                      color: '#666',
                      minWidth: '25px',
                      textAlign: 'center'
                    }}>
                      {Math.round(escala * 100)}%
                    </Typography>

                    <IconButton
                      size="small"
                      onClick={zoomIn}
                      sx={{
                        bgcolor: 'white',
                        boxShadow: 1,
                        width: 26,
                        height: 26,
                        minWidth: 26,
                        '&:hover': { bgcolor: '#f5f5f5' },
                        p: 0
                      }}
                    >
                      <ZoomIn fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={resetZoom}
                      sx={{
                        bgcolor: 'white',
                        boxShadow: 1,
                        width: 26,
                        height: 26,
                        minWidth: 26,
                        '&:hover': { bgcolor: '#f5f5f5' },
                        p: 0,
                        ml: 0.5
                      }}
                    >
                      <RestartAlt fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Paper>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{
        padding: isMobile ? "8px 24px" : "16px 24px",
        justifyContent: 'space-between',
        bgcolor: '#f8f9fa',
        borderTop: '1px solid #e5e7eb',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 0
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Typography variant="body1" sx={{
            fontWeight: 600,
            color: "#1e293b",
            fontSize: isMobile ? '14px' : '16px'
          }}>
            <ShoppingCart sx={{ fontSize: 16, mr: 0.5 }} />
            Total: ${layout?.tipo_seleccion === 'mesas_seleccion' ?
              (tarifa.precio * totalEntradasAsignadas).toLocaleString() :
              (tarifa.precio * cantidad).toLocaleString()}
          </Typography>
          {!isMobile && (
            <Typography variant="body2" sx={{
              color: "#6b7280",
              fontSize: '14px'
            }}>
              <LocalOffer sx={{ fontSize: 16, mr: 0.5 }} />
              Precio unitario: ${tarifa.precio}
            </Typography>
          )}
        </Box>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: isMobile ? 'column-reverse' : "row",
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          gap: isMobile ? 1 : 2,
          width: isMobile ? '100%' : 'auto',
        }}>
          <Button onClick={onClose} variant="outlined" sx={{
            borderRadius: '10px',
            border: "1px solid #5656FF",
            bgcolor: '#fff',
            color: '#5656FF',
            fontWeight: 600,
            padding: "5px 10px",
            width: isMobile ? '100%' : 'auto',
            '&:hover': { bgcolor: '#f1f5f9' },
            flex: isMobile ? 1 : 'none'
          }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={confirmarSeleccion}
            disabled={!puedeConfirmar()}
            sx={{
              borderRadius: '8px',
              bgcolor: '#5656FF',
              color: '#fff',
              fontWeight: 600,
              padding: "5px 10px",
              width: isMobile ? '100%' : 'auto',
              boxShadow: '0 1px 3px rgba(44,62,80,0.07)',
              '&:hover': { bgcolor: '#1d4ed8' },
              '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' },
              flex: isMobile ? 1 : 'none'
            }}
          >
            {
              layout?.tipo_seleccion === 'mesas_seleccion' ?
                `Confirmar ${totalEntradasAsignadas} ${totalEntradasAsignadas === 1 ? 'mesa' : 'mesas'}` :
                `Confirmar  ${asientosSeleccionados.length} ${asientosSeleccionados.length === 1 ? 'ticket' : 'tickets'}`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SelectUbicacionModal;